/**
 * mSTEM Shared Projects Modal (PRJ-05 / LLD UC 12). Presentational component
 * — Redux wiring lives in src/containers/shared-projects-modal.jsx.
 *
 * LLD UC 12 layout (DP-07 / DP-08):
 *   1. Search input (filters by name + sharedBy) + filter dropdown
 *      (Tất cả / Giáo viên / Lớp học / Cộng đồng)
 *   2. MstemTable with 6 columns: Tên dự án | Người chia sẻ | Loại thiết bị
 *      | Quyền (MstemBadge) | Thời gian chia sẻ | Thao tác
 *   3. Per-row actions:
 *        - "Xem"    (always visible)              — fake-load default project
 *                                                   read-only; toast on success
 *        - "Lưu copy" (ONLY when permission='copy') — duplicates to
 *                                                     mstem.myProjects with
 *                                                     "[Copy] " prefix
 *   4. Empty states: search-empty / load-error
 *   5. Bottom "Đóng" button
 *
 * Source data: mstem.sharedProjects[] (Phase 3 Wave 0 reducer + DP-08 seed).
 *
 * Reused i18n IDs (no re-extraction): gen.project.search.* and
 * gen.project.column.{name,type,actions} from Plan 03-03. Reused
 * gen.project.type.{robot,kit,none} from Plan 03-02 via plain object literal
 * (TYPE_LABEL_IDS — same pattern as open-project-modal.jsx) to satisfy the
 * phase-2-messages "No duplicate gen.* ids" test.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';

import styles from './shared-projects-modal.css';

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Dự án được chia sẻ với tôi',
        description: 'Shared Projects modal title (PRJ-05 LLD UC 12)',
        id: 'gen.share.received.title'
    },
    filterLabel: {
        defaultMessage: 'Lọc theo nguồn chia sẻ',
        description: 'Source filter dropdown aria-label (PRJ-05)',
        id: 'gen.share.received.filterLabel'
    },
    filterAll: {
        defaultMessage: 'Tất cả',
        description: 'Filter option: all sources (PRJ-05)',
        id: 'gen.share.received.filter.all'
    },
    filterTeacher: {
        defaultMessage: 'Giáo viên',
        description: 'Filter option: teacher source (PRJ-05)',
        id: 'gen.share.received.filter.teacher'
    },
    filterClass: {
        defaultMessage: 'Lớp học',
        description: 'Filter option: class source (PRJ-05)',
        id: 'gen.share.received.filter.class'
    },
    filterCommunity: {
        defaultMessage: 'Cộng đồng',
        description: 'Filter option: community source (PRJ-05)',
        id: 'gen.share.received.filter.community'
    },
    colSharedBy: {
        defaultMessage: 'Người chia sẻ',
        description: 'SharedProjectsModal table column: shared by (PRJ-05)',
        id: 'gen.share.column.sharedBy'
    },
    colPermission: {
        defaultMessage: 'Quyền',
        description: 'SharedProjectsModal table column: permission (PRJ-05)',
        id: 'gen.share.column.permission'
    },
    colSharedAt: {
        defaultMessage: 'Thời gian chia sẻ',
        description: 'SharedProjectsModal table column: shared timestamp (PRJ-05)',
        id: 'gen.share.column.sharedAt'
    },
    actionView: {
        defaultMessage: 'Xem',
        description: 'Row action button: view shared project read-only (PRJ-05)',
        id: 'gen.share.action.view'
    },
    actionSaveCopy: {
        defaultMessage: 'Lưu copy',
        description: 'Row action button: save a copy to my projects (PRJ-05)',
        id: 'gen.share.action.saveCopy'
    },
    closeButton: {
        defaultMessage: 'Đóng',
        description: 'Close shared projects modal (PRJ-05)',
        id: 'gen.share.received.close'
    },
    emptyFilter: {
        defaultMessage: 'Không có dự án phù hợp',
        description: 'Empty state for filter/search (PRJ-05 LLD UC 12)',
        id: 'gen.share.received.emptyFilter'
    },
    loadError: {
        defaultMessage: 'Không thể hiển thị danh sách dự án',
        description: 'Error state per LLD UC 12 (PRJ-05)',
        id: 'gen.share.received.loadError'
    },
    permissionCopyBadge: {
        defaultMessage: 'Sao chép',
        description: 'Permission badge label: allowed to copy (PRJ-05, distinct from PRJ-04 "Sao chép bản sao")',
        id: 'gen.share.permission.copyBadge'
    }
});

// Reused IDs (from Plan 03-02 new-project-modal.jsx + Plan 03-03
// open-project-modal.jsx + Plan 03-05 share-project-modal.jsx).
// babel-plugin-react-intl walks ONLY defineMessages() calls, so plain
// object literals do NOT trigger "Duplicate message id with different
// content" guard.
//
// `permissionViewBadge` reuses gen.share.permission.view ("Xem") from
// Plan 03-05 with the SAME default — safe to reuse the id. For the
// "Sao chép" copy badge we introduce a unique id
// (gen.share.permission.copyBadge) because Plan 03-05's
// gen.share.permission.copy = "Sao chép bản sao" (different value, so
// reusing the id would conflict at extraction time).
const REUSED_IDS = {
    searchPlaceholder: {id: 'gen.project.search.placeholder', defaultMessage: 'Tìm kiếm dự án...'},
    searchLabel: {id: 'gen.project.search.label', defaultMessage: 'Tìm kiếm dự án'},
    colName: {id: 'gen.project.column.name', defaultMessage: 'Tên dự án'},
    colType: {id: 'gen.project.column.type', defaultMessage: 'Loại thiết bị'},
    colActions: {id: 'gen.project.column.actions', defaultMessage: 'Thao tác'},
    permissionView: {id: 'gen.share.permission.view', defaultMessage: 'Xem'}
};

const TYPE_LABEL_IDS = {
    robot: {id: 'gen.project.type.robot', defaultMessage: 'Robot mSTEM'},
    kit: {id: 'gen.project.type.kit', defaultMessage: 'Kit SmartHome'},
    none: {id: 'gen.project.type.none', defaultMessage: 'Không kết nối thiết bị'}
};

const formatDate = function (iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
        return '—';
    }
};

const typeLabel = function (type, intl) {
    const descriptor = TYPE_LABEL_IDS[type] || TYPE_LABEL_IDS.none;
    return intl.formatMessage(descriptor);
};

const SharedProjectsModalComponent = ({
    intl,
    searchTerm,
    sourceFilter,
    filteredProjects,
    loadingError,
    onSearch,
    onFilter,
    onView,
    onSaveCopy,
    onClose
}) => {
    const emptyMessage = loadingError ?
        intl.formatMessage(messages.loadError) :
        intl.formatMessage(messages.emptyFilter);

    const columns = [
        {
            key: 'name',
            label: intl.formatMessage(REUSED_IDS.colName),
            width: '200px'
        },
        {
            key: 'sharedBy',
            label: <FormattedMessage {...messages.colSharedBy} />,
            width: '160px'
        },
        {
            key: 'type',
            label: intl.formatMessage(REUSED_IDS.colType),
            width: '140px',
            render: function renderTypeCell (row) {
                return typeLabel(row.type, intl);
            }
        },
        {
            key: 'permission',
            label: <FormattedMessage {...messages.colPermission} />,
            width: '100px',
            render: function renderPermissionCell (row) {
                const isCopy = row.permission === 'copy';
                return (
                    <MstemBadge variant={isCopy ? 'success' : 'info'}>
                        {isCopy ? (
                            <FormattedMessage {...messages.permissionCopyBadge} />
                        ) : (
                            <FormattedMessage {...REUSED_IDS.permissionView} />
                        )}
                    </MstemBadge>
                );
            }
        },
        {
            key: 'sharedAt',
            label: <FormattedMessage {...messages.colSharedAt} />,
            width: '140px',
            render: function renderSharedAtCell (row) {
                return formatDate(row.sharedAt);
            }
        },
        {
            key: 'actions',
            label: intl.formatMessage(REUSED_IDS.colActions),
            width: '170px',
            render: function renderActionsCell (row) {
                return (
                    <div className={styles.actionsCell}>
                        <button
                            className={classNames(styles.btnSecondary, styles.btnSmall)}
                            type="button"
                            onClick={() => onView(row.id)} // eslint-disable-line react/jsx-no-bind
                        >
                            <FormattedMessage {...messages.actionView} />
                        </button>
                        {row.permission === 'copy' && (
                            <button
                                className={classNames(styles.btnPrimary, styles.btnSmall)}
                                type="button"
                                onClick={() => onSaveCopy(row.id)} // eslint-disable-line react/jsx-no-bind
                            >
                                <FormattedMessage {...messages.actionSaveCopy} />
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.modalTitle)}
            id="sharedProjectsModal"
            onRequestClose={onClose}
        >
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    <input
                        aria-label={intl.formatMessage(REUSED_IDS.searchLabel)}
                        className={styles.searchInput}
                        placeholder={intl.formatMessage(REUSED_IDS.searchPlaceholder)}
                        type="search"
                        value={searchTerm}
                        onChange={onSearch}
                    />
                    <select
                        aria-label={intl.formatMessage(messages.filterLabel)}
                        className={styles.filterSelect}
                        value={sourceFilter}
                        onChange={onFilter}
                    >
                        <option value="all">{intl.formatMessage(messages.filterAll)}</option>
                        <option value="teacher">{intl.formatMessage(messages.filterTeacher)}</option>
                        <option value="class">{intl.formatMessage(messages.filterClass)}</option>
                        <option value="community">{intl.formatMessage(messages.filterCommunity)}</option>
                    </select>
                </div>
                <MstemTable
                    columns={columns}
                    data={filteredProjects}
                    emptyMessage={emptyMessage}
                />
                <div className={styles.footer}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={onClose}
                    >
                        <FormattedMessage {...messages.closeButton} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

SharedProjectsModalComponent.propTypes = {
    filteredProjects: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    intl: intlShape.isRequired,
    loadingError: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onFilter: PropTypes.func.isRequired,
    onSaveCopy: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    sourceFilter: PropTypes.string.isRequired
};

SharedProjectsModalComponent.defaultProps = {
    loadingError: false
};

export default injectIntl(SharedProjectsModalComponent);
